import React from 'react';
import { Box, ButtonBase, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import HubRounded from '@mui/icons-material/HubRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import {
  CodeBlock, EmptyState, MeterBar, Modal, Panel, SegmentedControl, StageFlow, StatusChip, ToolbarButton,
  type Stage,
} from '../../../ui-kit';
import {
  chooseProposal, confirmQuote, documentPicture, emailProposal, fetchMessages, fetchQuote, openDocument,
  openProposal, submitCarrier,
  type CqCarrier, type CqComparison, type CqDocument, type CqMessages, type CqMissing, type CqPackageKey,
  type CqProposal, type CqQuoteDetail,
} from '../../../services/carrierQuotingService';
import { useLive, when } from '../rocco/live';
import { BrandLogo } from './logos';
import { carrierChip, dollars, money, sizeOf, stageChip } from './status';

/** How long the last carrier's price stays on screen before the page moves to the comparison. */
const MOVE_ON_AFTER = 10;

type StepId = 'documents' | 'file' | 'carriers' | 'compare' | 'proposal';
const ORDER: StepId[] = ['documents', 'file', 'carriers', 'compare', 'proposal'];

/** What the button that moves on from each step says. */
const NEXT_LABEL: Record<StepId, string> = {
  documents: 'Review the customer file',
  file: 'See the carriers',
  carriers: 'Compare the quotes',
  compare: 'Build the proposal',
  proposal: '',
};

/** The step the work is on now: where the page opens, and where it moves to by itself. */
function currentStep(quote: CqQuoteDetail): StepId {
  if (quote.status === 'reading' || quote.status === 'failed') return 'documents';
  if (quote.status === 'review') return 'file';
  if (quote.status === 'quoting') return 'carriers';
  // A carrier's website waiting for a person, or submitted and not yet answered, keeps the
  // page on the carriers: its price is still to come and can change what we recommend.
  return quote.carriers.some((c) => c.status === 'needs_you' || c.status === 'queued') ? 'carriers' : 'compare';
}

/** Whether a step has anything to show yet. */
function reached(quote: CqQuoteDetail, step: StepId): boolean {
  switch (step) {
    case 'documents': return true;
    case 'file': return quote.file.length > 0;
    case 'carriers': return quote.carriers.length > 0;
    case 'compare': return Boolean(quote.comparison);
    default: return Boolean(quote.proposal);
  }
}

/** The five steps as cards, each saying where it stands. */
function stagesOf(quote: CqQuoteDetail): Stage[] {
  const read = quote.documents.filter((d) => d.status !== 'uploaded').length;
  const answered = quote.carriers.filter((c) => c.status !== 'queued' && c.status !== 'needs_you').length;
  const waiting = quote.carriers.filter((c) => c.status === 'needs_you');
  const pick = quote.proposal?.recommendation;
  const past = (...statuses: CqQuoteDetail['status'][]) => statuses.includes(quote.status);
  const flags = quote.comparison?.flags.length ?? 0;
  return [
    {
      id: 'documents', title: 'Documents', icon: <DescriptionRounded sx={{ fontSize: 18 }} />,
      setting: quote.status === 'reading' ? `Reading · ${read} of ${quote.documents.length}`
        : quote.status === 'failed' ? 'Could not be read' : `${quote.documents.length} read`,
      state: quote.status === 'failed' ? 'warn' : quote.status === 'reading' ? 'off' : 'on',
    },
    {
      id: 'file', title: 'Customer file', icon: <FactCheckRounded sx={{ fontSize: 18 }} />,
      setting: past('reading', 'failed') ? 'Not yet' : quote.status === 'review' ? 'Waiting for you' : 'Confirmed',
      state: quote.status === 'review' ? 'warn' : past('quoting', 'ready') ? 'on' : 'off',
    },
    {
      id: 'carriers', title: 'Carriers', icon: <HubRounded sx={{ fontSize: 18 }} />,
      setting: !quote.carriers.length ? 'Not yet'
        : waiting.length ? `${waiting[0].name} waiting for you`
          : quote.carriers.some((c) => c.status === 'queued') ? `Asking · ${answered} of ${quote.carriers.length}`
            : `${quote.carriers.filter((c) => c.status === 'quoted').length} of ${quote.carriers.length} quoted`,
      state: waiting.length ? 'warn'
        : quote.status === 'ready' && !quote.carriers.some((c) => c.status === 'queued') ? 'on' : 'off',
    },
    {
      id: 'compare', title: 'Compare', icon: <CompareArrowsRounded sx={{ fontSize: 18 }} />,
      setting: quote.comparison ? `${flags} thing${flags === 1 ? '' : 's'} to know` : 'Not yet',
      state: quote.comparison ? 'on' : 'off',
    },
    {
      id: 'proposal', title: 'Proposal', icon: <PictureAsPdfRounded sx={{ fontSize: 18 }} />,
      setting: pick ? `${pick.carrier_name} · ${money(pick.premium)}` : 'Not yet',
      state: pick ? 'on' : 'off',
    },
  ];
}

/**
 * One household's quote, on a page of its own: the five steps across the top,
 * each saying where it stands, and the chosen step's work below.
 *
 * The page follows the work: a quote in progress opens on the step it is at
 * and moves on as the quote does, so an upload can simply be watched. A
 * finished quote opens on the first step, to be read from the start. Clicking a step,
 * or the button at the top that names the next one, looks at that step instead, until
 * the work next moves. The human steps are taken here: confirming the
 * customer file before any carrier is asked, submitting a carrier's own
 * website when the rater does not reach it, and sending the proposal.
 */
export default function QuotePage({ quoteId }: { quoteId: string }) {
  const navigate = useNavigate();
  const fetcher = React.useCallback(() => fetchQuote(quoteId), [quoteId]);
  // Quick, because this page is where the carriers are watched answering.
  const { data: quote, error, reload: refresh } = useLive(fetcher, 2000);
  const [picked, setPicked] = React.useState<StepId | null>(null);
  const now = quote ? currentStep(quote) : 'documents';
  const loaded = Boolean(quote);
  const opening = React.useRef(true);
  const was = React.useRef<StepId>(now);
  // Seconds left before the page moves on to Compare by itself; null when it is not about to.
  const [moving, setMoving] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (!loaded) return;
    const before = was.current;
    was.current = now;
    if (opening.current) {
      // A quote that is already finished is being looked back at: start from the first step.
      // One still in progress opens where the work is, or where it is waiting for a person.
      opening.current = false;
      setPicked(now === 'compare' ? 'documents' : null);
      return;
    }
    if (before === 'carriers' && now === 'compare') {
      // The last price has just landed. Leave it on screen to be read, and say the page is
      // about to move on, rather than pulling it away the moment it arrives.
      setPicked('carriers');
      setMoving(MOVE_ON_AFTER);
      return;
    }
    // The work moved on: follow it again.
    setMoving(null);
    setPicked(null);
  }, [now, loaded]);
  React.useEffect(() => {
    if (moving === null) return undefined;
    if (moving <= 0) {
      setMoving(null);
      setPicked(null);
      window.scrollTo({ top: 0 });
      return undefined;
    }
    const id = window.setTimeout(() => setMoving((left) => (left === null ? null : left - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [moving]);
  // Going somewhere by hand calls off the move.
  const go = (to: StepId) => { setMoving(null); setPicked(to); };
  const step = picked ?? now;
  const back = () => navigate('/agents/carrier_quoting/quotes');

  if (error && !quote) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <ToolbarButton variant="ghost" icon={<ArrowBackRounded />} onClick={back}>Quotes</ToolbarButton>
        <EmptyState message={error} />
      </Stack>
    );
  }

  const facts = quote ? [
    [quote.drivers ? `${quote.drivers} driver${quote.drivers === 1 ? '' : 's'}` : null,
      quote.vehicles ? `${quote.vehicles} vehicle${quote.vehicles === 1 ? '' : 's'}` : null,
      quote.state].filter(Boolean).join(', '),
    quote.current_carrier
      ? `With ${quote.current_carrier} today${quote.current_premium != null ? ` at ${money(quote.current_premium)}` : ''}`
      : quote.status === 'reading' ? null : 'No policy in force',
    `Started ${when(quote.created_at)}${quote.by ? ` by ${quote.by}` : ''}`,
  ].filter(Boolean) as string[] : [];

  const next = ORDER[ORDER.indexOf(step) + 1] as StepId | undefined;
  const nextButton = quote && next ? (
    <ToolbarButton variant="brand" size="medium" icon={<ArrowForwardRounded />}
      disabled={!reached(quote, next)}
      tooltip={reached(quote, next) ? undefined : 'Not ready yet. The page moves on by itself when it is.'}
      onClick={() => { go(next); window.scrollTo({ top: 0 }); }}>
      {NEXT_LABEL[step]}
    </ToolbarButton>
  ) : null;

  return (
    <Stack spacing={2} sx={{ pb: 4 }}>
      {/* What this is, where it stands, and the way on: one tidy block. */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <ToolbarButton variant="outlined" icon={<ArrowBackRounded />} aria-label="Back to Quotes" tooltip="Back to Quotes"
          onClick={back} />
        <Stack sx={{ flex: 1, minWidth: 240 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography variant="h4" sx={(t) => ({ color: t.tokens.text.primary })}>
              {quote ? (quote.insured || 'Auto quote') : <Skeleton variant="text" width={220} />}
            </Typography>
            {quote ? <StatusChip {...stageChip(quote)} /> : null}
          </Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {quote ? facts.join('  ·  ') : <Skeleton variant="text" width={360} />}
          </Typography>
        </Stack>
        {nextButton}
      </Stack>

      {quote ? (
        <>
          <StageFlow stages={stagesOf(quote)} activeId={step} onOpen={(id) => go(id as StepId)} compact />
          <Progress quote={quote} />
          {step === 'documents' ? <Documents quote={quote} /> : null}
          {step === 'file' ? (
            quote.file.length ? <CustomerFile quote={quote} onChanged={refresh} />
              : <Waiting>The customer file is put together once every document has been read.</Waiting>
          ) : null}
          {step === 'carriers' ? (
            quote.carriers.length ? (
              <>
                <Carriers quote={quote} onChanged={refresh} />
                {moving !== null ? (
                  <MovingOn seconds={moving} onStay={() => setMoving(null)}
                    onGo={() => { go('compare'); window.scrollTo({ top: 0 }); }} />
                ) : null}
              </>
            )
              : <Waiting>No carrier has been asked yet. That happens once you confirm the customer file.</Waiting>
          ) : null}
          {step === 'compare' ? (
            quote.comparison ? <Comparison quote={quote} comparison={quote.comparison} />
              : <Waiting>The answers are lined up once every carrier asked through the rater has answered.</Waiting>
          ) : null}
          {step === 'proposal' ? (
            quote.proposal ? <Proposal quote={quote} proposal={quote.proposal} onChanged={refresh} />
              : <Waiting>The proposal is built once at least one carrier has quoted.</Waiting>
          ) : null}
        </>
      ) : <Loading />}
    </Stack>
  );
}

/**
 * Every carrier has answered and the page is about to move to the comparison.
 * It counts down in sight, so the move is expected, and can be called off:
 * the button at the top of the page is still there to go when ready.
 */
function MovingOn({ seconds, onStay, onGo }: { seconds: number; onStay: () => void; onGo: () => void }) {
  return (
    <Box role="status" sx={(t) => ({
      px: 1.5, py: 1.25, borderRadius: `${t.ds.radius.md}px`,
      border: `1px solid ${t.tokens.brand.primary}`, bgcolor: t.tokens.brand.primarySofter,
    })}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Typography variant="body2" sx={(t) => ({ flex: 1, minWidth: 220, color: t.tokens.text.primary })}>
            Every carrier has answered. Moving on to compare the quotes in{' '}
            <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {seconds} second{seconds === 1 ? '' : 's'}
            </Box>.
          </Typography>
          <ToolbarButton variant="outlined" onClick={onGo}>Compare now</ToolbarButton>
          <ToolbarButton variant="ghost" icon={<CloseRounded />} aria-label="Stay on this page"
            tooltip="Stay here. Use the button at the top when you are ready." onClick={onStay} />
        </Stack>
        <MeterBar value={((MOVE_ON_AFTER - seconds) / MOVE_ON_AFTER) * 100} tone="brand" />
      </Stack>
    </Box>
  );
}

/** A step the work has not reached yet. */
function Waiting({ children }: { children: React.ReactNode }) {
  return (
    <Panel title="Not yet">
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{children}</Typography>
    </Panel>
  );
}

function Note({ tone, children }: { tone: 'good' | 'bad' | 'warning' | 'neutral'; children: React.ReactNode }) {
  return (
    <Box sx={(t) => ({
      px: 1.5,
      py: 1.25,
      borderRadius: `${t.ds.radius.md}px`,
      bgcolor: {
        good: t.tokens.semantic.successSoft, bad: t.tokens.semantic.dangerSoft,
        warning: t.tokens.semantic.warningSoft, neutral: t.tokens.surface.sunken,
      }[tone],
      color: t.tokens.text.primary,
    })}>
      <Typography variant="body2" component="div">{children}</Typography>
    </Box>
  );
}

/**
 * One line on what is happening or what is needed, only while there is
 * something to say. A finished quote says it in the steps above.
 */
function Progress({ quote }: { quote: CqQuoteDetail }) {
  if (quote.status === 'failed') {
    return <Note tone="bad">{quote.error || 'None of the documents could be read.'}</Note>;
  }
  if (quote.status === 'review') {
    const needed = quote.missing.filter((m) => m.needed).length;
    return (
      <Note tone="warning">
        The customer file is ready for you. Look it over
        {quote.missing.length ? `, fill in what no document stated (${quote.missing.length}${needed ? `, ${needed} needed` : ''}),` : ''}
        {' '}then send it to the carriers. Nothing has left this workspace yet.
      </Note>
    );
  }
  if (quote.status === 'quoting') {
    const answered = quote.carriers.filter((c) => c.status !== 'queued').length;
    return (
      <Working label={`Asking ${quote.carriers.length} carriers. Each answers in its own time.`}
        done={answered} total={quote.carriers.length} unit="answered" />
    );
  }
  const late = quote.carriers.find((c) => c.status === 'queued');
  if (quote.status === 'ready' && late) {
    const answered = quote.carriers.filter((c) => c.status !== 'queued').length;
    return (
      <Working label={`Submitted to ${late.name}. Waiting for its price, which could change what we recommend.`}
        done={answered} total={quote.carriers.length} unit="answered" />
    );
  }
  const waiting = quote.carriers.find((c) => c.status === 'needs_you');
  if (quote.status === 'ready' && waiting) {
    return (
      <Note tone="warning">
        {waiting.name}'s website is filled in and waiting for you to submit. Its price could change what we recommend.
      </Note>
    );
  }
  return null;
}

function Working({ label, done, total, unit }: { label: string; done: number; total: number; unit: string }) {
  return (
    <Box sx={(t) => ({ px: 1.5, py: 1.25, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.surface.sunken })}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <StatusChip tone="good" label="Working" pulse />
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{label}</Typography>
        </Stack>
        <MeterBar value={(done / Math.max(1, total)) * 100} readout={`${done} of ${total} ${unit}`} tone="brand" />
      </Stack>
    </Box>
  );
}

// --- Documents -------------------------------------------------------------------

/** The files, each showing what is happening to it and then what was read from it. */
function Documents({ quote }: { quote: CqQuoteDetail }) {
  const total = quote.documents.length;
  const read = quote.documents.filter((d) => d.status === 'read').length;
  const failed = quote.documents.filter((d) => d.status === 'failed').length;
  const reading = quote.status === 'reading';
  return (
    <Panel
      title={reading ? 'Reading the documents' : 'The documents'}
      subtitle={reading
        ? 'All of them are read at the same time. Each card fills in as its document is finished.'
        : `${read} read${failed ? `, ${failed} could not be read` : ''} · what was taken from each one is listed on its card`}
    >
      <Stack spacing={2}>
        {reading ? (
          <Stack spacing={1}>
            <MeterBar value={((read + failed) / Math.max(1, total)) * 100}
              readout={`${read + failed} of ${total} read`} tone="brand" />
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              {[
                ['1', 'Works out what each document is'],
                ['2', 'Copies out the details a carrier needs'],
                ['3', 'Puts them together into one customer file'],
              ].map(([n, words]) => (
                <Typography key={n} variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
                  <Box component="span" sx={(t) => ({ color: t.tokens.brand.primary, fontWeight: 700 })}>{n}</Box>
                  {`  ${words}`}
                </Typography>
              ))}
            </Stack>
          </Stack>
        ) : null}
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {quote.documents.map((document) => <DocumentCard key={document.id} document={document} />)}
        </Box>
      </Stack>
    </Panel>
  );
}

function documentChip(document: CqDocument): { tone: 'good' | 'bad' | 'neutral'; label: string; pulse?: boolean } {
  if (document.status === 'failed') return { tone: 'bad', label: 'Could not be read' };
  if (document.status === 'read') {
    return { tone: 'good', label: document.seconds != null ? `Read in ${document.seconds}s` : 'Read' };
  }
  return document.reading ? { tone: 'neutral', label: 'Reading now', pulse: true } : { tone: 'neutral', label: 'Waiting its turn' };
}

function DocumentCard({ document }: { document: CqDocument }) {
  const [problem, setProblem] = React.useState<string | null>(null);
  const open = async () => {
    setProblem(null);
    try {
      await openDocument(document.id);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not open that document');
    }
  };
  const done = document.status === 'read';
  return (
    <Stack
      sx={(t) => ({
        minWidth: 0,
        overflow: 'hidden',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${document.status === 'failed' ? t.tokens.semantic.danger : t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      <Preview document={document} />
      <Stack spacing={1} sx={{ p: 1.5, flex: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={(t) => ({ color: t.tokens.text.primary })}>
              {done ? document.kind_label : document.status === 'failed' ? 'Not read' : 'Working out what this is'}
            </Typography>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
              {document.filename} · {sizeOf(document.size_bytes)}
            </Typography>
          </Stack>
          <ToolbarButton variant="ghost" icon={<OpenInNewRounded />} aria-label={`Open ${document.filename}`}
            tooltip="Open the document" onClick={open} />
        </Stack>
        <Box><StatusChip {...documentChip(document)} /></Box>

        {document.status === 'uploaded' ? (
          <Stack spacing={0.5} aria-hidden>
            <Skeleton variant="text" width="85%" />
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="78%" />
          </Stack>
        ) : null}
        {document.error || problem ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>
            {document.error || problem}
          </Typography>
        ) : null}
        {done ? (
          <Stack>
            {document.facts.map((fact) => (
              <Box key={fact.label} sx={(t) => ({
                display: 'grid', gridTemplateColumns: '96px 1fr', columnGap: 1, py: 0.5,
                borderTop: `1px solid ${t.tokens.border.subtle}`,
              })}>
                <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fact.label}</Typography>
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
                  {fact.value}
                </Typography>
              </Box>
            ))}
            {document.is_picture ? (
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, pt: 0.5 })}>
                Read by looking at the picture
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
}

const PREVIEW_HEIGHT = 132;

/**
 * The top of the document itself, at the head of its card, so every card
 * starts the same way: a photo as it is, a PDF's first page drawn by the
 * browser, and a plain band for anything that cannot be shown small.
 */
function Preview({ document }: { document: CqDocument }) {
  const [url, setUrl] = React.useState<string | null>(null);
  const pdf = document.filename.toLowerCase().endsWith('.pdf');
  const shown = document.is_picture || pdf;
  React.useEffect(() => {
    if (!shown) return undefined;
    let live = true;
    let made: string | null = null;
    documentPicture(document.id)
      .then((found) => { made = found; if (live) setUrl(found); else URL.revokeObjectURL(found); })
      .catch(() => undefined);
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [document.id, shown]);

  const frame = (t: import('@mui/material').Theme) => ({
    width: '100%', height: PREVIEW_HEIGHT, display: 'block', flexShrink: 0,
    borderBottom: `1px solid ${t.tokens.border.subtle}`, bgcolor: t.tokens.surface.sunken,
  });
  if (!shown) {
    return (
      <Box sx={(t) => ({ ...frame(t), display: 'grid', placeItems: 'center', color: t.tokens.text.tertiary })}>
        <DescriptionRounded sx={{ fontSize: 36 }} />
      </Box>
    );
  }
  if (!url) return <Skeleton variant="rectangular" height={PREVIEW_HEIGHT} aria-hidden />;
  if (document.is_picture) {
    return <Box component="img" src={url} alt="" sx={(t) => ({ ...frame(t), objectFit: 'cover' })} />;
  }
  // The browser's own PDF view, fitted to the card's width and showing the top of page one.
  // It is a picture here, not a reader: nothing in it can be clicked or scrolled.
  return (
    <Box sx={(t) => ({ ...frame(t), overflow: 'hidden', position: 'relative' })}>
      <Box component="iframe" title="" aria-hidden tabIndex={-1} scrolling="no"
        src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
        sx={{ width: '100%', height: PREVIEW_HEIGHT * 3, border: 0, pointerEvents: 'none', display: 'block' }} />
    </Box>
  );
}

// --- The customer file ------------------------------------------------------------

/** The household as the carriers will see it, every value with where it was read. */
function CustomerFile({ quote, onChanged }: { quote: CqQuoteDetail; onChanged: () => void }) {
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const review = quote.status === 'review';
  const stillNeeded = quote.missing.filter((m) => m.needed && !(answers[m.key] || '').trim());

  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await confirmQuote(quote.id, answers);
      onChanged();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not send the file to the carriers');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="The customer file"
      subtitle={review
        ? 'Put together from the documents. Under each value is the document it came from.'
        : `Confirmed${quote.approved_by ? ` by ${quote.approved_by}` : ''}${quote.approved_at ? ` · ${when(quote.approved_at)}` : ''}`}
      // The one thing to do here sits at the top, where it is seen before the file is scrolled.
      actions={review ? (
        <ToolbarButton variant="solid" size="medium" icon={<SendRounded />} onClick={send}
          loading={busy} loadingLabel="Sending" disabled={stillNeeded.length > 0}
          tooltip={stillNeeded.length ? `Still needed: ${stillNeeded.map((m) => m.label).join('; ')}` : undefined}>
          Confirm and ask the carriers
        </ToolbarButton>
      ) : undefined}
    >
      <Stack spacing={2}>
        {review ? (
          <Typography variant="caption" sx={(t) => ({
            color: problem ? t.tokens.semantic.danger : t.tokens.text.tertiary,
          })}>
            {problem || (stillNeeded.length
              ? `Before it can go: ${stillNeeded.map((m) => m.label).join('; ')}`
              : `Goes to the carriers through ${quote.rater}, under ${quote.agency}'s own logins. Nothing has been sent yet.`)}
          </Typography>
        ) : null}
        {review && quote.missing.length ? (
          <Stack spacing={1.25} sx={(t) => ({
            p: 1.5, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.semantic.warningSoft,
          })}>
            <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>
              What no document stated
            </Typography>
            {quote.missing.map((item) => (
              <Ask key={item.key} item={item} value={answers[item.key] ?? ''}
                onChange={(value) => setAnswers((have) => ({ ...have, [item.key]: value }))} />
            ))}
          </Stack>
        ) : null}

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
          {quote.file.map((section) => (
            <Stack
              key={section.title}
              sx={(t) => ({
                p: 1.5,
                minWidth: 0,
                borderRadius: `${t.ds.radius.md}px`,
                border: `1px solid ${t.tokens.border.subtle}`,
                bgcolor: t.tokens.surface.raised,
              })}
            >
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary, mb: 0.75 })}>
                {section.title}
              </Typography>
              {section.rows.map((row) => (
                <Box
                  key={row.label}
                  sx={(t) => ({
                    display: 'grid',
                    gridTemplateColumns: 'minmax(110px, 0.8fr) 1.4fr',
                    columnGap: 1.5,
                    py: 0.6,
                    borderTop: `1px solid ${t.tokens.border.subtle}`,
                  })}
                >
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{row.label}</Typography>
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={(t) => ({
                      overflowWrap: 'anywhere',
                      color: row.value ? t.tokens.text.primary : t.tokens.text.tertiary,
                    })}>
                      {row.value ?? 'Not stated'}
                    </Typography>
                    {row.source ? (
                      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                        {row.source}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              ))}
            </Stack>
          ))}
        </Box>

      </Stack>
    </Panel>
  );
}

/** One thing to fill in: what it is, why a carrier wants it, and a place to put it. */
function Ask({ item, value, onChange }: { item: CqMissing; value: string; onChange: (value: string) => void }) {
  return (
    <Box sx={{
      display: 'grid', columnGap: 1.5, rowGap: 0.5, alignItems: 'center',
      gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1.2fr) minmax(200px, 1fr)' },
    }}>
      <Stack sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>
            {item.label}
          </Typography>
          {item.needed ? <StatusChip tone="bad" label="Needed" /> : null}
        </Stack>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>{item.why}</Typography>
      </Stack>
      {item.kind === 'choice' ? (
        <Box>
          <SegmentedControl
            value={value}
            onChange={onChange}
            ariaLabel={item.label}
            options={(item.options ?? []).map((option) => ({ value: option, label: option }))}
          />
        </Box>
      ) : (
        <TextField
          id={`cq-${item.key}`}
          size="small"
          fullWidth
          type={item.kind === 'date' ? 'date' : 'text'}
          inputMode={item.kind === 'number' ? 'numeric' : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={item.kind === 'number' ? 'e.g. 12000' : 'Type it in, or leave it for later'}
          inputProps={{ 'aria-label': item.label }}
          sx={(t) => ({ bgcolor: t.tokens.surface.raised, borderRadius: `${t.ds.radius.sm}px` })}
        />
      )}
    </Box>
  );
}

// --- The carriers -------------------------------------------------------------------

/** Each carrier, as it answers. */
function Carriers({ quote, onChanged }: { quote: CqQuoteDetail; onChanged: () => void }) {
  const [messagesOf, setMessagesOf] = React.useState<CqCarrier | null>(null);
  const [detailOf, setDetailOf] = React.useState<CqCarrier | null>(null);
  const best = quote.proposal?.recommendation.carrier;
  // Every bar is drawn against the dearest quote, so their lengths compare.
  const highest = Math.max(0, ...quote.carriers.map((c) => c.premium ?? 0));
  return (
    <Panel
      title="The carriers"
      subtitle={`Asked through ${quote.rater}, under ${quote.agency}'s own carrier logins`}
      actions={(
        <Stack direction="row" alignItems="center" spacing={1}>
          <BrandLogo id={quote.rater_logo} name={quote.rater} size={24} />
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>{quote.rater}</Typography>
        </Stack>
      )}
    >
      <Stack>
        {quote.carriers.map((carrier) => (
          <CarrierRow key={carrier.id} carrier={carrier} best={carrier.carrier === best} highest={highest}
            onMessages={() => setMessagesOf(carrier)} onDetail={() => setDetailOf(carrier)} onChanged={onChanged} />
        ))}
      </Stack>
      {messagesOf ? <MessagesModal carrier={messagesOf} onClose={() => setMessagesOf(null)} /> : null}
      {detailOf ? <CarrierDetailModal carrier={detailOf} vehicles={quote.vehicle_names} onClose={() => setDetailOf(null)} /> : null}
    </Panel>
  );
}

/**
 * One carrier, on a row of its own: who it is, its price as a bar drawn
 * against the dearest quote, and what can be opened. The rows share their
 * columns, so names, bars and buttons each line up down the list.
 */
function CarrierRow({
  carrier, best, highest, onMessages, onDetail, onChanged,
}: {
  carrier: CqCarrier; best: boolean; highest: number;
  onMessages: () => void; onDetail: () => void; onChanged: () => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await submitCarrier(carrier.id);
      onChanged();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not submit that carrier');
    } finally {
      setBusy(false);
    }
  };
  const quoted = carrier.status === 'quoted';
  const share = quoted && highest ? Math.max(6, ((carrier.premium ?? 0) / highest) * 100) : 0;
  return (
    <Box
      sx={(t) => ({
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(210px, 1fr) minmax(240px, 2.2fr) 124px 236px' },
        columnGap: 3,
        rowGap: 1.25,
        alignItems: 'center',
        px: 1.5,
        py: 1.5,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: best ? t.tokens.brand.primarySofter : 'transparent',
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        '&:last-of-type': { borderBottom: 'none' },
      })}
    >
      {/* Who it is, and where it stands. */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
        <BrandLogo id={carrier.carrier} name={carrier.name} size={40} />
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={(t) => ({ color: t.tokens.text.primary })}>{carrier.name}</Typography>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <StatusChip {...carrierChip(carrier)} />
            {best ? <StatusChip tone="good" label="Recommended" /> : null}
          </Stack>
        </Stack>
      </Stack>

      {/* The answer. */}
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        {quoted ? (
          <>
            <Box sx={(t) => ({
              height: 14, borderRadius: `${t.ds.radius.xs}px`, bgcolor: t.tokens.surface.sunken, overflow: 'hidden',
            })}>
              <Box sx={(t) => ({
                width: `${share}%`, height: '100%', borderRadius: `${t.ds.radius.xs}px`,
                bgcolor: best ? t.tokens.brand.primary : t.tokens.text.tertiary,
                transition: `width ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              })} />
            </Box>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
              {[`${carrier.term_months} months, recommended cover`, `quote ${carrier.quote_number}`,
                carrier.seconds != null ? `answered in ${carrier.seconds}s` : null].filter(Boolean).join('  ·  ')}
            </Typography>
          </>
        ) : null}

        {carrier.status === 'queued' ? (
          <>
            <Skeleton variant="rounded" height={14} aria-hidden />
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{carrier.step}</Typography>
          </>
        ) : null}

        {carrier.status === 'referred' || carrier.status === 'declined' ? (
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {carrier.reason}{carrier.code ? ` (${carrier.code})` : ''}
          </Typography>
        ) : null}

        {carrier.status === 'needs_you' ? (
          <>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
              {carrier.name} is not on the rater, so its own website is filled in for you. Check it and submit.
            </Typography>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {(carrier.portal?.screens ?? []).map((screen) => `${screen.name}: ${screen.fields} fields`).join('  ·  ')}
            </Typography>
            {problem ? (
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
            ) : null}
          </>
        ) : null}

        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
          {[carrier.program, `through ${carrier.via}`, `producer code ${carrier.login}`].filter(Boolean).join('  ·  ')}
        </Typography>
      </Stack>

      {/* The price, in a column of its own so the figures line up down the list. */}
      <Typography variant="h4" sx={(t) => ({
        color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums', textAlign: { md: 'right' },
      })}>
        {quoted ? money(carrier.premium) : ''}
      </Typography>

      {/* What can be done or opened. */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, justifyContent: { md: 'flex-end' } }}>
        {carrier.status === 'needs_you' ? (
          <ToolbarButton variant="solid" size="medium" icon={<SendRounded />} onClick={submit}
            loading={busy} loadingLabel="Submitting">
            Submit to {carrier.name}
          </ToolbarButton>
        ) : (
          <>
            <ToolbarButton variant="outlined" onClick={onDetail} disabled={!quoted}>View details</ToolbarButton>
            <ToolbarButton variant="outlined" onClick={onMessages} disabled={!carrier.has_messages}
              tooltip={carrier.has_messages ? 'What was sent to the carrier and what came back'
                : 'Quoted on the carrier’s own website, so there are no messages'}>
              Messages
            </ToolbarButton>
          </>
        )}
      </Stack>
    </Box>
  );
}

/** What went to a carrier and what came back, exactly as sent. */
function MessagesModal({ carrier, onClose }: { carrier: CqCarrier; onClose: () => void }) {
  const [data, setData] = React.useState<CqMessages | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [show, setShow] = React.useState<'response' | 'request'>('response');
  React.useEffect(() => {
    let live = true;
    fetchMessages(carrier.id)
      .then((found) => { if (live) { setData(found); if (!found.response) setShow('request'); } })
      .catch((err) => { if (live) setError(err instanceof Error ? err.message : 'Could not load the messages'); });
    return () => { live = false; };
  }, [carrier.id]);
  const code = data ? (show === 'response' ? data.response : data.request) : null;
  return (
    <Modal
      open
      onClose={onClose}
      title={`${carrier.name} · the messages`}
      subtitle={data ? `${data.format} · as exchanged with the carrier's rating service` : undefined}
      size="lg"
      actions={(
        <SegmentedControl
          value={show}
          onChange={(value) => setShow(value as 'response' | 'request')}
          ariaLabel="Which message"
          options={[{ value: 'request', label: 'What we sent' }, { value: 'response', label: 'What came back' }]}
        />
      )}
      footer={<ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>}
    >
      {error ? <EmptyState message={error} /> : null}
      {!data && !error ? <Skeleton variant="rounded" height={420} aria-hidden /> : null}
      {data ? (
        code
          ? <CodeBlock code={code} lineNumbers copyable maxHeight={520} aria-label={`${carrier.name} ${show}`} />
          : <EmptyState message="The carrier has not answered yet." />
      ) : null}
    </Modal>
  );
}

const PACKAGE_LABELS: Record<CqPackageKey, string> = {
  current: 'Same cover as today', recommended: 'Recommended', higher: 'Higher protection',
};

const coverLine = (limits: number[], deductible: number | null): string => {
  if (deductible != null) return `${dollars(deductible)} deductible`;
  return limits.map((value) => dollars(value)).join(' / ');
};

/** One carrier's price, coverage by coverage, for each package it priced. */
function CarrierDetailModal({ carrier, vehicles, onClose }: { carrier: CqCarrier; vehicles: string[]; onClose: () => void }) {
  const keys = (Object.keys(PACKAGE_LABELS) as CqPackageKey[]).filter((key) => carrier.packages[key]);
  const [key, setKey] = React.useState<CqPackageKey>(keys.includes('recommended') ? 'recommended' : keys[0]);
  const priced = carrier.packages[key];
  return (
    <Modal
      open
      onClose={onClose}
      title={`${carrier.name} · quote details`}
      subtitle={[carrier.company, carrier.program, carrier.naic ? `NAIC ${carrier.naic}` : null,
        carrier.quote_number ? `quote ${carrier.quote_number}` : null,
        carrier.valid_until ? `held until ${carrier.valid_until}` : null].filter(Boolean).join(' · ')}
      size="md"
      actions={(
        <SegmentedControl value={key} onChange={(value) => setKey(value as CqPackageKey)} ariaLabel="Which package"
          options={keys.map((k) => ({ value: k, label: PACKAGE_LABELS[k] }))} />
      )}
      footer={<ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>}
    >
      {priced ? (
        <Stack spacing={2}>
          {priced.vehicles.map((vehicle) => (
            <Stack key={vehicle.index}>
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>
                {vehicles[vehicle.index - 1] || `Vehicle ${vehicle.index}`}
              </Typography>
              {vehicle.coverages.map((cover) => (
                <Line key={cover.code} label={cover.name} middle={coverLine(cover.limits, cover.deductible)}
                  value={money(cover.premium)} />
              ))}
              <Line label="Vehicle total" value={money(vehicle.subtotal)} strong />
            </Stack>
          ))}
          <Stack>
            <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>Discounts and fees</Typography>
            {priced.discounts.map((discount) => (
              <Line key={discount.code} label={discount.name} middle={`${discount.percent}%`}
                value={`−${money(discount.amount)}`} />
            ))}
            {priced.fees.map((fee) => <Line key={fee.code} label={fee.name} value={money(fee.amount)} />)}
            <Line label={`Premium for ${carrier.term_months} months`} value={money(priced.total)} strong />
          </Stack>
          <Stack>
            <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>Ways to pay</Typography>
            {priced.pay_plans.map((plan) => (
              <Line key={plan.code} label={plan.name}
                middle={plan.payments ? `${money(plan.down)} down, then ${plan.payments} × ${money(plan.each)}` : plan.note}
                value={money(plan.total)} />
            ))}
          </Stack>
          {priced.notes.length || priced.available.length ? (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>From the carrier</Typography>
              {priced.notes.map((note) => (
                <Typography key={note} variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>{note}</Typography>
              ))}
              {priced.available.map((offer) => (
                <Typography key={offer.name} variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
                  {offer.name}: {offer.detail}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Stack>
      ) : <EmptyState message="This carrier did not price that package." />}
    </Modal>
  );
}

function Line({ label, middle, value, strong }: { label: string; middle?: string; value: string; strong?: boolean }) {
  return (
    <Box sx={(t) => ({
      display: 'grid',
      gridTemplateColumns: 'minmax(140px, 1.3fr) minmax(120px, 1.2fr) auto',
      columnGap: 1.5,
      py: 0.6,
      borderTop: `1px solid ${strong ? t.tokens.border.default : t.tokens.border.subtle}`,
    })}>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: strong ? 600 : 400 })}>{label}</Typography>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{middle ?? ''}</Typography>
      <Typography variant="body2" sx={(t) => ({
        color: t.tokens.text.primary, fontWeight: strong ? 600 : 400, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
      })}>
        {value}
      </Typography>
    </Box>
  );
}

// --- Compare ------------------------------------------------------------------------

/** The answers side by side, then what to know about them. */
function Comparison({ quote, comparison }: { quote: CqQuoteDetail; comparison: CqComparison }) {
  const keys = comparison.packages.map((p) => p.key);
  const [key, setKey] = React.useState<CqPackageKey>(keys.includes('recommended') ? 'recommended' : keys[0]);
  const table = comparison.tables[key] ?? comparison.tables[keys[0]];
  const pick = quote.proposal?.recommendation.carrier ?? comparison.recommendation?.carrier;
  const template = table
    ? { xs: '1fr', md: `minmax(200px, 1.3fr) repeat(${table.columns.length}, minmax(130px, 1fr))` } : undefined;
  let group = '';

  return (
    <>
      {table && template ? (
        <Panel
          title="Side by side"
          subtitle={`${comparison.counts.quoted} of ${comparison.counts.asked} carriers quoted · a figure that differs from the rest is in amber`}
          tools={(
            <SegmentedControl value={key} onChange={(value) => setKey(value as CqPackageKey)} ariaLabel="Which package"
              options={comparison.packages.map((p) => ({ value: p.key, label: p.name }))} />
          )}
        >
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Box sx={{ minWidth: { md: 200 + 140 * table.columns.length } }}>
              <Box sx={(t) => ({
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: template,
                borderBottom: `1px solid ${t.tokens.border.default}`,
              })}>
                <span />
                {table.columns.map((column) => (
                  <Stack key={column.key} spacing={0.5} sx={(t) => ({
                    minWidth: 0, px: 1, py: 1,
                    borderRadius: `${t.ds.radius.sm}px ${t.ds.radius.sm}px 0 0`,
                    bgcolor: column.key === pick ? t.tokens.brand.primarySofter : 'transparent',
                  })}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <BrandLogo id={column.key === 'today' ? null : column.key} name={column.label} size={22} />
                      <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>
                        {column.label}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={(t) => ({
                      color: column.key === pick ? t.tokens.brand.primary : t.tokens.text.tertiary,
                    })}>
                      {column.key === pick ? 'On the proposal' : column.sub}
                    </Typography>
                  </Stack>
                ))}
              </Box>
              {table.rows.map((row) => {
                const heading = row.group !== group;
                group = row.group;
                const total = row.key === 'premium';
                return (
                  <React.Fragment key={row.key}>
                    {heading ? (
                      <Typography variant="overline" sx={(t) => ({ display: 'block', color: t.tokens.text.tertiary, pt: 1.5 })}>
                        {row.group}
                      </Typography>
                    ) : null}
                    <Box sx={(t) => ({
                      display: 'grid',
                      gridTemplateColumns: template,
                      alignItems: 'stretch',
                      borderBottom: `1px solid ${t.tokens.border.subtle}`,
                    })}>
                      <Typography variant="body2" sx={(t) => ({
                        color: t.tokens.text.primary, fontWeight: total ? 600 : 400, py: 0.75,
                      })}>
                        {row.label}
                      </Typography>
                      {table.columns.map((column) => {
                        const cell = row.cells[column.key];
                        return (
                          <Box key={column.key} sx={(t) => ({
                            minWidth: 0, px: 1, py: 0.75,
                            bgcolor: column.key === pick ? t.tokens.brand.primarySofter : 'transparent',
                          })}>
                            <Typography variant="caption"
                              sx={(t) => ({ color: t.tokens.text.tertiary, display: { xs: 'block', md: 'none' } })}>
                              {column.label}
                            </Typography>
                            <Typography variant={total ? 'subtitle1' : 'body2'} sx={(t) => ({
                              overflowWrap: 'anywhere',
                              fontVariantNumeric: 'tabular-nums',
                              fontWeight: total || cell?.differs ? 600 : 400,
                              color: cell?.differs ? t.tokens.semantic.warning : t.tokens.text.primary,
                            })}>
                              {cell?.text ?? '—'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </Panel>
      ) : null}

      {comparison.flags.length ? (
        <Panel title="What to know" subtitle="Found by rules, from the customer file and the carriers' answers">
          <Stack>
            {comparison.flags.map((flag) => (
              <Box
                key={flag.title + flag.detail}
                sx={(t) => ({
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '110px minmax(200px, 1fr) 2fr' },
                  columnGap: 2,
                  rowGap: 0.25,
                  alignItems: 'baseline',
                  py: 1,
                  borderBottom: `1px solid ${t.tokens.border.subtle}`,
                  '&:last-of-type': { borderBottom: 'none' },
                })}
              >
                <Box>
                  <StatusChip tone={flag.severity === 'important' ? 'bad' : 'neutral'}
                    label={flag.severity === 'important' ? 'Important' : 'Note'} />
                </Box>
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{flag.title}</Typography>
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{flag.detail}</Typography>
              </Box>
            ))}
          </Stack>
        </Panel>
      ) : null}
    </>
  );
}

/** A card that is one of several to choose from: outlined, and filled in when it is the one chosen. */
const choice = (t: import('@mui/material').Theme, on: boolean) => ({
  display: 'block',
  textAlign: 'left' as const,
  p: 1.5,
  borderRadius: `${t.ds.radius.md}px`,
  border: `1px solid ${on ? t.tokens.brand.primary : t.tokens.border.subtle}`,
  bgcolor: on ? t.tokens.brand.primarySofter : t.tokens.surface.raised,
  transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
  '&:hover': { borderColor: t.tokens.brand.primary },
  '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing, outline: 'none' },
});

// --- The proposal -------------------------------------------------------------------

/**
 * What goes on the proposal, and sending it. The rules pick a carrier and
 * the recommended cover; a person can put a different carrier or a different
 * option on it, and the proposal's own words change to match.
 */
function Proposal({ quote, proposal, onChanged }: { quote: CqQuoteDetail; proposal: CqProposal; onChanged: () => void }) {
  const [busy, setBusy] = React.useState<string | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [emailing, setEmailing] = React.useState(false);
  const pick = proposal.recommendation;
  const chosenPackage = (pick.package ?? 'recommended') as CqPackageKey;

  const change = async (carrier: string | null, pkg: CqPackageKey | null, what: string) => {
    setBusy(what);
    setProblem(null);
    try {
      await chooseProposal(quote.id, carrier, pkg);
      onChanged();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not change the proposal');
    } finally {
      setBusy(null);
    }
  };
  const open = async () => {
    setBusy('open');
    setProblem(null);
    try {
      await openProposal(quote.id);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not build the proposal');
    } finally {
      setBusy(null);
    }
  };

  // One carrier to choose: its logo, its price for the option chosen, and a tick when it is the one.
  const carrierCard = (carrier: CqProposal['carriers'][number]) => {
    const on = carrier.carrier === pick.carrier;
    return (
      <ButtonBase
        key={carrier.carrier}
        onClick={() => change(carrier.carrier, chosenPackage, 'carrier')}
        disabled={busy !== null || on}
        aria-pressed={on}
        sx={(t) => choice(t, on)}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <BrandLogo id={carrier.carrier} name={carrier.name} size={44} />
          <Stack sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" sx={(t) => ({ color: t.tokens.text.primary })}>{carrier.name}</Typography>
            <Typography variant="h5" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
              {money(carrier.premiums[chosenPackage] ?? null)}
            </Typography>
            {/* A line every card keeps, so they stay the same height: ours says so, the rest leave it empty. */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 20 }}>
              {carrier.carrier === proposal.rule.carrier ? (
                <>
                  <StarRounded sx={(t) => ({ fontSize: 16, color: t.tokens.semantic.warning })} />
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>Our pick</Typography>
                </>
              ) : null}
            </Stack>
          </Stack>
          {/* The tick means chosen, and only that. */}
          {on ? (
            <Tooltip title="On the proposal">
              <CheckCircleRounded aria-label="On the proposal"
                sx={(t) => ({ fontSize: 22, alignSelf: 'flex-start', color: t.tokens.brand.primary })} />
            </Tooltip>
          ) : null}
        </Stack>
      </ButtonBase>
    );
  };
  // Ours first and set apart, so it is always in the same place whichever is chosen.
  const ours = proposal.carriers.find((carrier) => carrier.carrier === proposal.rule.carrier);
  const others = proposal.carriers.filter((carrier) => carrier.carrier !== proposal.rule.carrier);

  return (
    <>
      <Panel
        title="1. Choose the carrier"
        subtitle={proposal.by_rule
          ? 'Our pick is first: the lowest price for the recommended cover. The tick shows the carrier on the proposal. Click another to change it.'
          : `Changed by hand. Our pick, ${proposal.rule.carrier_name}, is first; the tick shows the carrier on the proposal.`}
        actions={!proposal.by_rule ? (
          <ToolbarButton variant="outlined" onClick={() => change(null, null, 'reset')} loading={busy === 'reset'}>
            Back to our pick
          </ToolbarButton>
        ) : undefined}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          {ours ? (
            <>
              <Box sx={{ display: 'grid', flex: { md: '0 0 240px' } }}>{carrierCard(ours)}</Box>
              <Box aria-hidden sx={(t) => ({
                flexShrink: 0, alignSelf: 'stretch',
                width: { xs: '100%', md: '1px' }, height: { xs: '1px', md: 'auto' },
                bgcolor: t.tokens.border.default,
              })} />
            </>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
            {others.map(carrierCard)}
          </Box>
        </Stack>
      </Panel>

      <Panel
        title="2. Choose the option to recommend"
        subtitle={`All three options go on the proposal, priced with ${pick.carrier_name}. Click the one it should recommend.`}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: `repeat(${proposal.options.length}, 1fr)` } }}>
            {proposal.options.map((option) => (
              <ButtonBase
                key={option.package}
                onClick={() => change(pick.carrier, option.package, option.package)}
                disabled={busy !== null || option.recommended}
                aria-pressed={option.recommended}
                sx={(t) => choice(t, option.recommended)}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>{option.name}</Typography>
                  <Typography variant="h3" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
                    {money(option.premium)}
                  </Typography>
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                    {pick.term_months} months
                    {option.against_today != null
                      ? ` · ${dollars(Math.abs(option.against_today))} ${option.against_today > 0 ? 'more' : 'less'} than today`
                      : ''}
                  </Typography>
                  <Box sx={{ minHeight: 24, pt: 0.5 }}>
                    {option.recommended ? <StatusChip tone="good" label="Recommended on the proposal" /> : null}
                  </Box>
                </Stack>
              </ButtonBase>
            ))}
          </Box>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>What the proposal will say</Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
              {pick.reasons.map((reason) => (
                <Typography key={reason} component="li" variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
                  {reason}
                </Typography>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Panel>

      <Panel title="3. Send it" subtitle={`A PDF with ${quote.agency}'s name and logo. Nothing is bound by sending it.`}>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <ToolbarButton variant="solid" size="medium" icon={<MailOutlineRounded />} onClick={() => setEmailing(true)}
              disabled={busy !== null}>
              Share by email
            </ToolbarButton>
            <ToolbarButton variant="outlined" size="medium" icon={<PictureAsPdfRounded />} onClick={open}
              loading={busy === 'open'} loadingLabel="Building the proposal">
              Open the proposal
            </ToolbarButton>
            {problem ? (
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
            ) : null}
          </Stack>
          {proposal.sent.length ? (
            <Stack>
              {proposal.sent.slice().reverse().map((sent) => (
                <Stack key={sent.at + sent.to} direction="row" spacing={1} alignItems="center"
                  sx={(t) => ({ py: 0.75, borderTop: `1px solid ${t.tokens.border.subtle}`, flexWrap: 'wrap', rowGap: 0.5 })}>
                  <StatusChip tone="good" label="Sent" />
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
                    {sent.to}
                  </Typography>
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                    {when(sent.at)}{sent.by ? ` by ${sent.by}` : ''}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              Not sent to anyone yet.
            </Typography>
          )}
        </Stack>
      </Panel>

      {emailing ? (
        <EmailModal quote={quote} proposal={proposal}
          onClose={() => setEmailing(false)} onSent={() => { setEmailing(false); onChanged(); }} />
      ) : null}
    </>
  );
}

/** The email the proposal goes out with: written for you, yours to change, sent when you say. */
function EmailModal({
  quote, proposal, onClose, onSent,
}: { quote: CqQuoteDetail; proposal: CqProposal; onClose: () => void; onSent: () => void }) {
  const draft = proposal.email ?? { to: '', subject: '', body: '' };
  const [to, setTo] = React.useState(draft.to);
  const [subject, setSubject] = React.useState(draft.subject);
  const [body, setBody] = React.useState(draft.body);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const valid = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(to.trim()) && subject.trim() && body.trim();
  const pick = proposal.recommendation;

  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await emailProposal(quote.id, { to: to.trim(), subject: subject.trim(), body });
      onSent();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not send the email');
    } finally {
      setBusy(false);
    }
  };
  const preview = async () => {
    try {
      await openProposal(quote.id);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not build the proposal');
    }
  };
  const field = (t: import('@mui/material').Theme) => ({ bgcolor: t.tokens.surface.raised });

  return (
    <Modal
      open
      onClose={onClose}
      busy={busy}
      title="Share the proposal by email"
      subtitle={`${pick.carrier_name} · ${pick.package_name ?? 'Recommended'} · ${money(pick.premium)} for ${pick.term_months} months`}
      size="md"
      footer={(
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', flexWrap: 'wrap', rowGap: 1 }}>
          <Typography variant="caption" sx={(t) => ({
            flex: 1, minWidth: 180, color: problem ? t.tokens.semantic.danger : t.tokens.text.tertiary,
          })}>
            {problem || `Sent from ${quote.agency}. Replies come to you.`}
          </Typography>
          <ToolbarButton variant="ghost" onClick={onClose} disabled={busy}>Cancel</ToolbarButton>
          <ToolbarButton variant="solid" size="medium" icon={<SendRounded />} onClick={send}
            loading={busy} loadingLabel="Sending" disabled={!valid}>
            Send email
          </ToolbarButton>
        </Stack>
      )}
    >
      <Stack spacing={1.5}>
        <TextField id="cq-email-to" label="To" size="small" fullWidth type="email" value={to}
          onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com"
          helperText={draft.to ? 'From the customer file' : 'No email was on the documents; type the customer’s address'}
          sx={field} />
        <TextField id="cq-email-subject" label="Subject" size="small" fullWidth value={subject}
          onChange={(e) => setSubject(e.target.value)} sx={field} />
        <TextField id="cq-email-body" label="Message" size="small" fullWidth multiline minRows={10} maxRows={18}
          value={body} onChange={(e) => setBody(e.target.value)} sx={field} />
        <Stack direction="row" alignItems="center" spacing={1} sx={(t) => ({
          p: 1, borderRadius: `${t.ds.radius.sm}px`, border: `1px solid ${t.tokens.border.subtle}`,
          flexWrap: 'wrap', rowGap: 0.5,
        })}>
          <AttachFileRounded sx={(t) => ({ fontSize: 18, color: t.tokens.text.tertiary })} />
          <PictureAsPdfRounded sx={(t) => ({ fontSize: 18, color: t.tokens.semantic.danger })} />
          <Typography variant="body2" sx={(t) => ({ flex: 1, minWidth: 0, color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
            Auto insurance proposal for {quote.insured || 'the household'}.pdf
          </Typography>
          <ToolbarButton variant="ghost" icon={<OpenInNewRounded />} onClick={preview}>Open</ToolbarButton>
        </Stack>
      </Stack>
    </Modal>
  );
}

function Loading() {
  return (
    <Stack spacing={1.5} aria-hidden>
      <Skeleton variant="rounded" height={72} />
      <Skeleton variant="rounded" height={140} />
      <Skeleton variant="rounded" height={260} />
    </Stack>
  );
}
